import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Box,
  Typography,
  Chip,
  Collapse,
  IconButton,
  Grid,
  useTheme,
} from "@mui/material";
import {
  ExpandMore,
  ExpandLess,
  BuildCircle,
  RocketLaunch,
  CheckCircle,
  Error,
  PauseCircle,
} from "@mui/icons-material";
import UnifiedAppCard from "./UnifiedAppCard";

const statusGroups = [
  {
    key: "building",
    label: "Building",
    color: "#f59e0b",
    bgColor: "rgba(245, 158, 11, 0.1)",
    icon: BuildCircle,
    defaultExpanded: true,
    animate: true,
  },
  {
    key: "deploying",
    label: "Deploying",
    color: "#F97316",
    bgColor: "rgba(234, 88, 12, 0.1)",
    icon: RocketLaunch,
    defaultExpanded: true,
    animate: true,
  },
  {
    key: "live",
    label: "Live",
    color: "#22c55e",
    bgColor: "rgba(34, 197, 94, 0.1)",
    icon: CheckCircle,
    defaultExpanded: true,
    animate: false,
  },
  {
    key: "error",
    label: "Errors",
    color: "#ef4444",
    bgColor: "rgba(239, 68, 68, 0.1)",
    icon: Error,
    defaultExpanded: true,
    animate: false,
  },
  {
    key: "idle",
    label: "Sleeping",
    color: "#64748b",
    bgColor: "rgba(100, 116, 139, 0.1)",
    icon: PauseCircle,
    defaultExpanded: false,
    animate: false,
  },
];

const pulseAnimation = {
  scale: [1, 1.15, 1],
  opacity: [0.7, 1, 0.7],
};

/**
 * Section header component
 */
const SectionHeader = ({
  group,
  count,
  expanded,
  onToggle,
  isDarkMode,
}) => {
  const Icon = group.icon;

  return (
    <Box
      onClick={onToggle}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        py: 1.5,
        px: 1,
        cursor: "pointer",
        borderRadius: "8px",
        transition: "background 0.2s ease",
        "&:hover": {
          background: isDarkMode ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
        },
      }}
    >
      {/* Status dot with optional pulse */}
      <Box
        component={group.animate ? motion.div : "div"}
        animate={group.animate ? pulseAnimation : {}}
        transition={group.animate ? { duration: 2, repeat: Infinity } : {}}
        sx={{
          width: 10,
          height: 10,
          borderRadius: "50%",
          background: group.color,
          boxShadow: group.animate ? `0 0 8px ${group.color}` : "none",
        }}
      />

      {/* Icon */}
      <Icon sx={{ fontSize: "1.1rem", color: group.color }} />

      {/* Label */}
      <Typography
        variant="subtitle1"
        sx={{
          fontWeight: 600,
          color: isDarkMode ? "#e2e8f0" : "#1e293b",
        }}
      >
        {group.label}
      </Typography>

      {/* Count badge */}
      <Chip
        size="small"
        label={count}
        sx={{
          height: 22,
          minWidth: 28,
          fontSize: "0.75rem",
          fontWeight: 600,
          background: group.bgColor,
          color: group.color,
          border: "none",
        }}
      />

      {/* Expand/Collapse */}
      <Box sx={{ ml: "auto" }}>
        <IconButton size="small" sx={{ color: isDarkMode ? "#64748b" : "#94a3b8" }}>
          {expanded ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </Box>
    </Box>
  );
};

/**
 * Activity section with collapsible content
 */
const ActivitySection = ({
  group,
  apps,
  onOpenPipeline,
  onRestart,
  onRebuild,
  onOpenUrl,
  onCardClick,
  isDarkMode,
}) => {
  const [expanded, setExpanded] = useState(group.defaultExpanded);

  if (apps.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        mb: 2,
        borderRadius: "12px",
        border: `1px solid ${isDarkMode ? "#334155" : "#e2e8f0"}`,
        overflow: "hidden",
        background: isDarkMode ? "rgba(30, 41, 59, 0.5)" : "#fff",
      }}
    >
      <SectionHeader
        group={group}
        count={apps.length}
        expanded={expanded}
        onToggle={() => setExpanded(!expanded)}
        isDarkMode={isDarkMode}
      />

      <Collapse in={expanded}>
        <Box sx={{ p: 2, pt: 0 }}>
          <Grid container spacing={2}>
            <AnimatePresence mode="popLayout">
              {apps.map((app) => (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  key={app.id}
                  component={motion.div}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  layout
                >
                  <UnifiedAppCard
                    app={app}
                    onOpenPipeline={onOpenPipeline}
                    onRestart={onRestart}
                    onRebuild={onRebuild}
                    onOpenUrl={onOpenUrl}
                    onClick={onCardClick}
                  />
                </Grid>
              ))}
            </AnimatePresence>
          </Grid>
        </Box>
      </Collapse>
    </Box>
  );
};

/**
 * Group apps by their overall status
 */
const groupByStatus = (apps) => {
  const groups = {
    building: [],
    deploying: [],
    live: [],
    error: [],
    idle: [],
  };

  apps.forEach((app) => {
    const status = app.overallStatus;
    if (groups[status]) {
      groups[status].push(app);
    } else {
      groups.idle.push(app);
    }
  });

  return groups;
};

/**
 * Activity stream view - apps grouped by status
 */
export function ActivityStreamView({
  apps,
  loading,
  onOpenPipeline,
  onRestart,
  onRebuild,
  onOpenUrl,
  onCardClick,
}) {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  const groupedApps = useMemo(() => groupByStatus(apps || []), [apps]);

  if (loading) {
    return (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <Typography color="text.secondary">Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {statusGroups.map((group) => (
        <ActivitySection
          key={group.key}
          group={group}
          apps={groupedApps[group.key] || []}
          onOpenPipeline={onOpenPipeline}
          onRestart={onRestart}
          onRebuild={onRebuild}
          onOpenUrl={onOpenUrl}
          onCardClick={onCardClick}
          isDarkMode={isDarkMode}
        />
      ))}
    </Box>
  );
}

export default ActivityStreamView;
